import { useState, useEffect } from 'react';

export const useFormData = (selectedVersion) => {
  const [formData, setFormData] = useState({
    openapi: selectedVersion || '',
    info: {
      title: '',
      description: '',
      summary: selectedVersion === "3.1.0" ? '' : undefined,
      version: '',
      termsOfService: '',
      contact: { name: '', url: '', email: '' },
      license: { name: '', url: '', identifier: selectedVersion === "3.1.0" ? '' : null },
    },
    externalDocs: { description: '', url: '' },
    servers: [],
    tags: [],
    paths: {},
    components: {
      schemas: {},
      headers: {},
      examples: {},
      requestBodies: {},
      responses: {},
      parameters: {},
      securitySchemes: {},
    },
  });

  const setOpenAPIVersion = (version) => {
    setFormData((prevData) => ({
      ...prevData,
      openapi: version,
    }));
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      openapi: selectedVersion,
      info: {
        ...prev.info,
        summary: selectedVersion === "3.1.0" ? prev.info.summary || '' : undefined,
        license: {
          ...prev.info.license,
          identifier: selectedVersion === "3.1.0" ? prev.info.license?.identifier || '' : undefined,
        },
      },
    }));
  }, [selectedVersion]);

  const handleInputChange = (section, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value,
      },
    }));
  };

  const handleNestedInputChange = (section, subsection, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [subsection]: {
          ...prevData[section][subsection],
          [field]: value,
        },
      },
    }));
  };

  const handleArrayChange = (arrayName, index, field, value) => {
    setFormData((prevData) => {
      const updatedArray = [...prevData[arrayName]];
      if (index !== -1) {
        updatedArray[index] = { ...updatedArray[index], [field]: value };
      } else {
        return { ...prevData, [arrayName]: value };
      }
      return { ...prevData, [arrayName]: updatedArray };
    });
  };

  const addServer = () => {
    setFormData((prevData) => ({
      ...prevData,
      servers: [...prevData.servers, { url: '', description: '' }],
    }));
  };

  const removeServer = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      servers: prevData.servers.filter((_, i) => i !== index),
    }));
  };

  const addTag = (newTag) => {
    setFormData((prevData) => ({
      ...prevData,
      tags: [...prevData.tags, newTag],
    }));
  };

  const handleTagChange = (index, field, value) => {
    setFormData((prevData) => {
      const updatedTags = [...prevData.tags];
      updatedTags[index] = { ...updatedTags[index], [field]: value };
      return { ...prevData, tags: updatedTags };
    });
  };

  const handleTagExternalDocsChange = (index, field, value) => {
    setFormData((prevData) => {
      const updatedTags = [...prevData.tags];
      updatedTags[index].externalDocs = {
        ...updatedTags[index].externalDocs,
        [field]: value,
      };
      return { ...prevData, tags: updatedTags };
    });
  };

  const handleSecuritySchemeChange = (schemeName, schemeData) => {
    setFormData((prevData) => {
      const updatedComponents = { ...prevData.components };
      if (schemeData === undefined) {
        delete updatedComponents.securitySchemes[schemeName];
      } else {
        updatedComponents.securitySchemes = {
          ...updatedComponents.securitySchemes,
          [schemeName]: schemeData,
        };
      }
      return {
        ...prevData,
        components: updatedComponents,
      };
    });
  };

  const removeTag = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      tags: prevData.tags.filter((_, i) => i !== index),
    }));
  };

  const addSchema = (name, newSchema) => {
    const { name: _, ...schemaDetails } = newSchema;
    setFormData((prevData) => ({
      ...prevData,
      components: {
        ...prevData.components,
        schemas: {
          ...prevData.components.schemas,
          [name]: schemaDetails,
        },
      },
    }));
  };

  const editSchema = (key, updatedSchema) => {
    setFormData((prevData) => ({
      ...prevData,
      components: {
        ...prevData.components,
        schemas: {
          ...prevData.components.schemas,
          [key]: updatedSchema,
        },
      },
    }));
  };


  const removeSchema = (schemaName) => {
    setFormData((prevData) => {
      const updatedSchemas = { ...prevData.components.schemas };
      delete updatedSchemas[schemaName];
      return {
        ...prevData,
        components: {
          ...prevData.components,
          schemas: updatedSchemas,
        },
      };
    });
  };

  const addHeader = (name, headerData) => {
    setFormData((prevData) => ({
      ...prevData,
      components: {
        ...prevData.components,
        headers: {
          ...prevData.components.headers,
          [name]: headerData,
        },
      },
    }));
  };

  const editHeader = (key, updatedHeaderData) => {
    setFormData((prevData) => {
      const updatedHeaders = { ...prevData.components.headers };

      if (updatedHeaderData.name && key !== updatedHeaderData.name) {
        delete updatedHeaders[key];
        updatedHeaders[updatedHeaderData.name] = updatedHeaderData;
      } else {
        updatedHeaders[key] = updatedHeaderData;
      }

      return {
        ...prevData,
        components: {
          ...prevData.components,
          headers: updatedHeaders,
        },
      };
    });
  };

  const removeHeader = (headerName) => {
    setFormData((prevData) => {
      const updatedHeaders = { ...prevData.components.headers };
      delete updatedHeaders[headerName];
      return {
        ...prevData,
        components: {
          ...prevData.components,
          headers: updatedHeaders,
        },
      };
    });
  };

  const addExample = (newExample) => {
    const parsedValue = typeof newExample.value === "string" ? JSON.parse(newExample.value) : newExample.value;
    setFormData((prev) => ({
      ...prev,
      components: {
        ...prev.components,
        examples: {
          ...prev.components.examples,
          [newExample.name]: { value: parsedValue },
        },
      },
    }));
  };

  const editExample = (name, updatedExample) => {
    const parsedValue = typeof updatedExample.value === "string" ? JSON.parse(updatedExample.value) : updatedExample.value;
    setFormData((prev) => ({
      ...prev,
      components: {
        ...prev.components,
        examples: {
          ...prev.components.examples,
          [name]: { value: parsedValue },
        },
      },
    }));
  };

  const removeExample = (name) => {
    setFormData((prev) => {
      const updatedExamples = { ...prev.components.examples };
      delete updatedExamples[name];
      return {
        ...prev,
        components: {
          ...prev.components,
          examples: updatedExamples,
        },
      };
    });
  };

  const addResponse = (name, response) => {
    setFormData((prev) => ({
      ...prev,
      components: {
        ...prev.components,
        responses: {
          ...prev.components.responses,
          [name]: response,
        },
      },
    }));
  };

  const editResponse = (name, updatedResponse) => {
    setFormData((prev) => ({
      ...prev,
      components: {
        ...prev.components,
        responses: {
          ...prev.components.responses,
          [name]: updatedResponse,
        },
      },
    }));
  };

  const removeResponse = (name) => {
    setFormData((prev) => {
      const updatedResponses = { ...prev.components.responses };
      delete updatedResponses[name];
      return {
        ...prev,
        components: {
          ...prev.components,
          responses: updatedResponses,
        },
      };
    });
  };

  const addRequestBody = (name, request) => {
    setFormData((prev) => ({
      ...prev,
      components: {
        ...prev.components,
        requestBodies: {
          ...prev.components.requestBodies,
          [name]: request,
        },
      },
    }));
  };

  const editRequestBody = (name, updatedRequest) => {
    setFormData((prev) => ({
      ...prev,
      components: {
        ...prev.components,
        requestBodies: {
          ...prev.components.requestBodies,
          [name]: updatedRequest,
        },
      },
    }));
  };

  const removeRequestBody = (name) => {
    setFormData((prev) => {
      const updatedRequests = { ...prev.components.requestBodies };
      delete updatedRequests[name];
      return {
        ...prev,
        components: {
          ...prev.components,
          requestBodies: updatedRequests,
        },
      };
    });
  };

  const addParameter = (name, parameterData) => {
    setFormData((prevData) => ({
      ...prevData,
      components: {
        ...prevData.components,
        parameters: {
          ...prevData.components.parameters,
          [name]: parameterData,
        },
      },
    }));
  };

  const editParameter = (key, updatedParameterData) => {
    setFormData((prevData) => {
      const updatedParameters = { ...prevData.components.parameters };

      if (updatedParameterData.name && key !== updatedParameterData.name) {
        delete updatedParameters[key];
        updatedParameters[updatedParameterData.name] = updatedParameterData;
      } else {
        updatedParameters[key] = updatedParameterData;
      }

      return {
        ...prevData,
        components: {
          ...prevData.components,
          parameters: updatedParameters,
        },
      };
    });
  };

  const removeParameter = (parameterName) => {
    setFormData((prevData) => {
      const updatedParameters = { ...prevData.components.parameters };
      delete updatedParameters[parameterName];
      return {
        ...prevData,
        components: {
          ...prevData.components,
          parameters: updatedParameters,
        },
      };
    });
  };

  const addPath = (name, operations) => {
    setFormData((prevData) => ({
      ...prevData,
      paths: {
        ...prevData.paths,
        [name]: operations.reduce((acc, operation) => {
          acc[operation.verb] = {
            summary: operation.summary || '',
            description: operation.description || '',
            tags: operation.tags || [],
            parameters: operation.parameters.map((param) =>
              typeof param === 'string'
                ? { $ref: `#/components/parameters/${param}` }
                : param
            ),
            ...(operation.verb.toLowerCase() !== 'get'
              && operation.verb.toLowerCase() !== 'delete' &&
              operation.requestBody && !operation.acknowledgedMissingRequestBody &&
              typeof operation.requestBody === 'string'
              ? { requestBody: { $ref: `#/components/requestBodies/${operation.requestBody}` } }
              : operation.verb.toLowerCase() !== 'get' && operation.verb.toLowerCase() !== 'delete' &&
                operation.requestBody &&
                !operation.acknowledgedMissingRequestBody &&
                operation.requestBody.$ref
                ? { requestBody: operation.requestBody }
                : {}),
            responses: typeof operation.responses === 'object' && !Array.isArray(operation.responses)
              ? operation.responses
              : Array.isArray(operation.responses)
                ? operation.responses.reduce((resAcc, { httpStatus, response }) => {
                  if (httpStatus && response) {
                    resAcc[httpStatus] = { $ref: `#/components/responses/${response}` };
                  } else {
                    console.warn('Invalid response during add:', { httpStatus, response });
                  }
                  return resAcc;
                }, {})
                : {},
            operationId: operation.operationId || '',
            deprecated: operation.deprecated || false,
            security: Array.isArray(operation.security)
              ? operation.security.map((sec) => {
                const [key] = Object.keys(sec);
                return {
                  [key]: sec[key] || [],
                };
              })
              : [],
          };

          return acc;
        }, {}),
      },
    }));
  };

  const editPath = (name, updatedOperations) => {
    setFormData((prevData) => ({
      ...prevData,
      paths: {
        ...prevData.paths,
        [name]: updatedOperations.reduce((acc, operation) => {
          acc[operation.verb] = {
            summary: operation.summary || '',
            description: operation.description || '',
            tags: operation.tags || [],
            parameters: operation.parameters.map((param) =>
              typeof param === 'string'
                ? { $ref: `#/components/parameters/${param}` }
                : param
            ),
            ...(operation.verb.toLowerCase() !== 'get' &&
              operation.verb.toLowerCase() !== 'delete' &&
              operation.requestBody &&
              !operation.acknowledgedMissingRequestBody &&
              typeof operation.requestBody === 'string'
              ? { requestBody: { $ref: `#/components/requestBodies/${operation.requestBody}` } }
              : operation.verb.toLowerCase() !== 'get' && operation.verb.toLowerCase() !== 'delete' &&
                operation.requestBody &&
                !operation.acknowledgedMissingRequestBody &&
                operation.requestBody.$ref
                ? { requestBody: operation.requestBody }
                : {}),
            responses: typeof operation.responses === 'object' && !Array.isArray(operation.responses)
              ? operation.responses
              : Array.isArray(operation.responses)
                ? operation.responses.reduce((resAcc, { httpStatus, response }) => {
                  if (httpStatus && response) {
                    resAcc[httpStatus] = { $ref: `#/components/responses/${response}` };
                  } else {
                    console.warn('Invalid response during edit:', { httpStatus, response });
                  }
                  return resAcc;
                }, {})
                : {},
            operationId: operation.operationId || '',
            deprecated: operation.deprecated || false,
            security: Array.isArray(operation.security)
              ? operation.security.map((sec) => {
                const [key] = Object.keys(sec);
                return {
                  [key]: sec[key] || [],
                };
              })
              : [],
          };

          return acc;
        }, {}),
      },
    }));
  };

  const removePath = (name) => {
    setFormData((prevData) => {
      if (!prevData.paths) return prevData;
      const updatedPaths = { ...prevData.paths };
      if (updatedPaths[name]) {
        delete updatedPaths[name];
      }

      return {
        ...prevData,
        paths: updatedPaths,
      };
    });
  };


  return {
    formData,
    addHeader,
    editHeader,
    removeHeader,
    setFormData,
    setOpenAPIVersion,
    handleInputChange,
    handleNestedInputChange,
    handleArrayChange,
    addServer,
    removeServer,
    addTag,
    handleTagChange,
    handleTagExternalDocsChange,
    handleSecuritySchemeChange,
    removeTag,
    addSchema,
    editSchema,
    removeSchema,
    addExample,
    removeExample,
    editExample,
    addResponse,
    editResponse,
    removeResponse,
    addRequestBody,
    editRequestBody,
    removeRequestBody,
    addParameter,
    editParameter,
    removeParameter,
    addPath,
    editPath,
    removePath,
  };
};
